import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { businessStartQuestionQuery, YesNoEtc } from '@/__generated__/businessStartQuestionQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { AnswerList, CTA, ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertIsStartBusinessDirectly } from '@/graphql/mutation/survey/business';
import { startBusinessQuery } from '@/graphql/query/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';
import { hasEmployeeAtom } from '@/lib/stores/survey/common';

const answers: { value: YesNoEtc; label: string }[] = [
  { value: 'yes', label: '직접 창업했어요' },
  { value: 'no', label: '인수했어요 / 물려받았어요' }
];

export const StartBusinessContainer = ({ queryRef }: { queryRef: PreloadedQuery<businessStartQuestionQuery> }) => {
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const router = useRouter();
  const { getSurveyBusinessAnswer } = usePreloadedQuery<businessStartQuestionQuery>(startBusinessQuery, queryRef);
  const [upsertAnswer, isLoading] = useUpsertBusinessAnswer(upsertIsStartBusinessDirectly);
  const hasEmployee = useAtomValue(hasEmployeeAtom);

  const [selectedAnswer, setSelectedAnswer] = useState<YesNoEtc | undefined>(undefined);

  useEffect(() => {
    const prevAnswer = getSurveyBusinessAnswer?.businessAnswer?.[0]?.isStartBusinessDirectly;
    if (prevAnswer) {
      setSelectedAnswer(prevAnswer);
    }
  }, [getSurveyBusinessAnswer?.businessAnswer]);

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_start-business-directly_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_start-business-directly_confirm', eventAttributes);

            upsertAnswer(
              { isStartBusinessDirectly: selectedAnswer },
              {
                onCompleted: () => {
                  if (selectedAnswer === 'yes') {
                    router.push(SURVEY_PAGES.동종업종_사업여부);
                    return;
                  } else if (hasEmployee) {
                    router.push(SURVEY_PAGES.승계직원);
                  } else {
                    router.push(SURVEY_PAGES.사업장_근무여부);
                  }
                }
              }
            );
          }}
        >
          <ProgressBar step={1} />

          <h1 className={common.content_title}>직접 창업하셨나요? 아니면 다른 분의 사업을 인수했거나 물려받았나요?</h1>
          <AnswerList
            answers={answers}
            checkedValue={selectedAnswer}
            onChange={(value) => {
              sendClickEvent(`survey_start-business-directly_${value}`, eventAttributes);

              setSelectedAnswer(value);
            }}
          />

          {!!selectedAnswer && (
            <CTA type={'submit'} disabled={!selectedAnswer || isLoading}>
              다음
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};
